import AppDatePicker from '@nxsys/components/AppDatePicker';
import AppInput from '@nxsys/components/AppInput';
import AppSelect from '@nxsys/components/AppSelect';
import AppWorkingPatternSchedule from '@nxsys/components/AppWorkingPatternSchedule';
import { SCHEDULE_DAYS } from '@nxsys/components/AppWorkingPatternSchedule/days';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import jwtAxios from '@nxsys/services/axios';
import { endPoints } from '@nxsys/services/endpoints';
import { IWorkingPattern } from '@nxsys/types/models/workingPattern';
import { Alert, Col, Form, FormInstance, Row } from 'antd';
import { debounce } from 'lodash';
import moment, { Moment } from 'moment';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  toWorkingPatternOption,
  WorkingPatternOption,
  WorkingPatternValues,
} from './workingPatternUtils';

// NX-4948: ported from the Old UI (nxsys-fe-payroll RegistrationInfo.jsx).
// The backend pro-rates a salaried employee over the working pattern, clipped
// to payrollCalculationStart/End — without them a mid-period starter/leaver is
// paid the full period and a non 5-day week falls back to the default calc.

type Props = {
  form: FormInstance;
  /** Patterns are scoped to the payment company / employer. */
  companyId?: string | null;
  /**
   * The company's default pattern — already on the company record
   * (company search / employee detail join it), so no fetch is needed to
   * pre-select it.
   */
  companyDefaultPattern?: IWorkingPattern | null;
  /** Lower bound for the calculation start date. */
  dateOfJoining?: Moment | null;
  /** Edit flow: what is saved, restored when switching back to that company. */
  stored?: { companyId?: string | null; values: WorkingPatternValues };
};

const WorkingPatternFields = ({
  form,
  companyId,
  companyDefaultPattern,
  dateOfJoining,
  stored,
}: Props) => {
  const { messages } = useTypedIntl();
  const [patterns, setPatterns] = useState<IWorkingPattern[]>([]);
  const [loading, setLoading] = useState(false);
  const previousCompanyId = useRef(companyId);
  const selected: WorkingPatternOption | null = Form.useWatch(
    'workingPattern',
    form,
  );
  const selectedRef = useRef(selected);
  selectedRef.current = selected;
  const payrollCalculationStart: Moment | null = Form.useWatch(
    'payrollCalculationStart',
    form,
  );

  // The list is only needed to pick from, so load it when the dropdown opens
  // (Old UI parity). Only the latest request may write the list.
  const requestId = useRef(0);
  const fetchPatterns = async (searchText = '') => {
    if (!companyId) return;
    const id = ++requestId.current;
    setLoading(true);
    try {
      // `search` matches the pattern name server-side (ILIKE).
      const res: { data?: { data?: IWorkingPattern[] } } = await jwtAxios.get(
        `${endPoints.company.workingPattern}/${companyId}`,
        {
          params: {
            page: 0,
            size: 100,
            ...(searchText.trim() ? { search: searchText.trim() } : {}),
          },
        },
      );
      if (id === requestId.current) setPatterns(res.data?.data ?? []);
    } catch {
      if (id === requestId.current) setPatterns([]);
    } finally {
      if (id === requestId.current) setLoading(false);
    }
  };

  const [search, setSearch] = useState('');
  // Debounced so typing does not fire a request per keystroke; the ref keeps
  // it calling the latest fetchPatterns (current companyId).
  const fetchPatternsRef = useRef(fetchPatterns);
  fetchPatternsRef.current = fetchPatterns;
  const debouncedSearch = useMemo(
    () => debounce((text: string) => fetchPatternsRef.current(text), 400),
    [],
  );
  useEffect(() => () => debouncedSearch.cancel(), [debouncedSearch]);

  // Company picked / changed: no fetch — default comes with the company.
  useEffect(() => {
    debouncedSearch.cancel();
    setSearch('');
    // The previous company's list no longer applies.
    requestId.current++;
    setPatterns([]);
    setLoading(false);
    if (!companyId) return;

    // The first load (edit form hydrating) is not a change.
    const companyChanged =
      !!previousCompanyId.current && previousCompanyId.current !== companyId;
    previousCompanyId.current = companyId;
    const companyDefault = toWorkingPatternOption(companyDefaultPattern);

    if (!companyChanged) return;
    if (stored && stored.companyId === companyId) {
      // Back to the saved company: restore the saved pattern + dates (an
      // empty pattern then shows the default below).
      form.setFieldsValue(stored.values);
    } else {
      // Another company: its default, and the dates start over.
      handleChange(companyDefault);
    }
  }, [companyId]);

  // No pattern of its own = the employee follows the company default, so
  // show it. Only the pattern is set — the stored dates are left alone so
  // opening/refetching the form never wipes them.
  useEffect(() => {
    if (!companyId || !companyDefaultPattern) return;
    if (form.getFieldValue('workingPattern')) return;
    form.setFieldsValue({
      workingPattern: toWorkingPatternOption(companyDefaultPattern),
    });
  }, [companyId, companyDefaultPattern?.id, selected?.value]);

  // The start date is bounded by the join date, which can change after the
  // start date was picked (edit form) — re-check it.
  useEffect(() => {
    if (form.getFieldValue('payrollCalculationStart')) {
      form.validateFields(['payrollCalculationStart']).catch(() => undefined);
    }
  }, [dateOfJoining?.valueOf()]);

  // Switching to a different pattern (or clearing it) resets the payroll
  // calculation dates — they belong to the previous pattern.
  const handleChange = (option: WorkingPatternOption | null | undefined) => {
    // Form.Item has already written the new value by the time this runs, so
    // compare against the value from the last render.
    if (option && option.value === selectedRef.current?.value) {
      form.setFieldsValue({ workingPattern: option });
      return;
    }
    form.setFieldsValue({
      workingPattern: option ?? null,
      payrollCalculationStart: null,
      payrollCalculationEnd: null,
    });
  };

  const options = useMemo(
    () =>
      patterns.map((p) => toWorkingPatternOption(p) as WorkingPatternOption),
    [patterns],
  );

  const pattern = selected?.fullData;
  // Payroll falls back to the company default when the employee has no
  // pattern, so "no pattern" is only a real choice without a default.
  const hasCompanyDefault = !!companyDefaultPattern;

  return (
    <>
      <div className="text-[16px] font-semibold mb-4">
        {messages['common.workingPattern']}
      </div>
      <Row gutter={24}>
        <Col xs={24} sm={12}>
          <Form.Item
            name="workingPattern"
            label={messages['common.workingPattern']}
          >
            <AppSelect
              options={options}
              loading={loading}
              allowClear={!hasCompanyDefault}
              showSearch
              // Filtered server-side, so keep every option the API returns.
              filterOption={false}
              searchValue={search}
              onSearch={(text: string) => {
                setSearch(text);
                debouncedSearch(text);
              }}
              disabled={!companyId}
              notFoundContent={
                companyId
                  ? 'No working patterns found'
                  : 'Please select a company first'
              }
              placeholder={messages['form.select']}
              className="w-full !min-w-0"
              onOpenChange={(open: boolean) => {
                debouncedSearch.cancel();
                setSearch('');
                if (open) fetchPatterns();
              }}
              onChange={(option: unknown) =>
                handleChange(option as WorkingPatternOption | undefined)
              }
            />
          </Form.Item>
        </Col>
        {pattern?.isDefault && (
          <Col xs={24} sm={12} className="flex items-center">
            <Alert
              type="warning"
              showIcon
              message="This is Default Working Pattern"
              className="w-full mb-6"
            />
          </Col>
        )}
      </Row>

      {pattern && (
        <>
          {/* Bottom-aligned: the FTE labels wrap to two lines, so top
              alignment left the inputs of a pair at different heights. */}
          <Row gutter={24} align="bottom">
            <Col xs={24} sm={12}>
              <Form.Item label="Working Days (Full-Time Equivalent)">
                <AppInput value={pattern.workingDaysFTE ?? 0} disabled />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item label="Working Days (Actual)">
                <AppInput value={pattern.workingDays ?? 0} disabled />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item label="Working Hours (Full-Time Equivalent)">
                <AppInput value={pattern.workingHoursFTE ?? 0} disabled />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item label="Working Hours (Actual)">
                <AppInput value={pattern.workingHours ?? 0} disabled />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                name="payrollCalculationStart"
                label={
                  messages['common.payrollCalculationStartDate'] ??
                  'Payroll Calculation Start Date'
                }
                rules={[
                  {
                    validator: async (_, value: Moment | null) => {
                      if (
                        value &&
                        dateOfJoining &&
                        value.isBefore(dateOfJoining, 'day')
                      ) {
                        throw new Error(
                          'Payroll Calculation Start Date must be on or after the Employment Start Date',
                        );
                      }
                    },
                  },
                ]}
              >
                <AppDatePicker
                  placeholder={messages['form.selectDate']}
                  format={'DD/MM/YYYY'}
                  disabledDate={(d: Moment) =>
                    d.isBefore(moment(dateOfJoining || undefined), 'day')
                  }
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                name="payrollCalculationEnd"
                label={
                  messages['common.payrollCalculationEndDate'] ??
                  'Payroll Calculation End Date'
                }
                dependencies={['payrollCalculationStart']}
                rules={[
                  ({ getFieldValue }) => ({
                    validator: async (_, value: Moment | null) => {
                      const start: Moment | null = getFieldValue(
                        'payrollCalculationStart',
                      );
                      if (value && start && value.isBefore(start, 'day')) {
                        throw new Error(
                          'Payroll Calculation End Date must be on or after the Start Date',
                        );
                      }
                    },
                  }),
                ]}
              >
                <AppDatePicker
                  placeholder={messages['form.selectDate']}
                  format={'DD/MM/YYYY'}
                  disabledDate={(d: Moment) =>
                    !!payrollCalculationStart &&
                    d.isBefore(payrollCalculationStart, 'day')
                  }
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item label="Schedule per week">
            <AppWorkingPatternSchedule
              days={Object.fromEntries(
                SCHEDULE_DAYS.map((day) => [
                  day,
                  pattern.workingPattern?.[day] === 'Y',
                ]),
              )}
            />
          </Form.Item>
        </>
      )}
    </>
  );
};

export default WorkingPatternFields;
